/**
 * Routing utilities for delivery route optimisation and navigation
 * Uses OSRM for route optimisation and Google Maps for navigation
 */

/**
 * Optimise route order using OSRM Trip API
 * Returns the optimal order to visit all waypoints
 *
 * @param {Array<{lat: number, lng: number}>} waypoints - Array of coordinates
 * @param {Object} options - Options
 * @param {boolean} options.roundtrip - Whether to return to start (default: false)
 * @returns {Promise<{orderedIndices: number[], distance: number, duration: number}>}
 */
export async function optimiseRoute(waypoints, options = {}) {
  const { roundtrip = false } = options

  if (!waypoints || waypoints.length < 2) {
    return {
      orderedIndices: waypoints?.map((_, i) => i) || [],
      distance: 0,
      duration: 0
    }
  }

  try {
    // Format coordinates for OSRM: lng,lat;lng,lat;...
    const coords = waypoints.map(w => `${w.lng},${w.lat}`).join(';')

    // Use OSRM demo server (free, rate-limited)
    // For production, consider self-hosting OSRM
    const url = `https://router.project-osrm.org/trip/v1/driving/${coords}?` +
      `roundtrip=${roundtrip}&source=first&destination=last&overview=false`

    const response = await fetch(url)
    const data = await response.json()

    if (data.code !== 'Ok') {
      console.error('OSRM error:', data.code, data.message)
      // Fall back to original order
      return {
        orderedIndices: waypoints.map((_, i) => i),
        distance: 0,
        duration: 0,
        error: data.message
      }
    }

    // Extract optimised order from waypoints
    const orderedIndices = data.waypoints.map(w => w.waypoint_index)

    // Get total distance and duration from the trip
    const trip = data.trips?.[0]
    const distance = trip?.distance ? Math.round(trip.distance / 1000 * 10) / 10 : 0 // km
    const duration = trip?.duration ? Math.round(trip.duration / 60) : 0 // minutes

    return {
      orderedIndices,
      distance,
      duration
    }
  } catch (error) {
    console.error('Route optimisation failed:', error)
    // Fall back to original order
    return {
      orderedIndices: waypoints.map((_, i) => i),
      distance: 0,
      duration: 0,
      error: error.message
    }
  }
}

/**
 * Generate a Google Maps directions URL with waypoints
 *
 * @param {Array<{lat: number, lng: number, address?: string}>} stops - Ordered list of stops
 * @returns {string} Google Maps URL
 */
export function generateGoogleMapsUrl(stops) {
  if (!stops || stops.length === 0) {
    return 'https://www.google.com/maps'
  }

  if (stops.length === 1) {
    // Single destination
    const dest = stops[0]
    const destStr = dest.address || `${dest.lat},${dest.lng}`
    return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destStr)}&travelmode=driving`
  }

  // Multiple stops: origin + destination + waypoints
  const origin = stops[0]
  const destination = stops[stops.length - 1]
  const waypoints = stops.slice(1, -1)

  let url = 'https://www.google.com/maps/dir/?api=1'

  // Origin - use address if available, else coordinates
  const originStr = origin.address || `${origin.lat},${origin.lng}`
  url += `&origin=${encodeURIComponent(originStr)}`

  // Destination
  const destStr = destination.address || `${destination.lat},${destination.lng}`
  url += `&destination=${encodeURIComponent(destStr)}`

  // Waypoints (if any)
  if (waypoints.length > 0) {
    const waypointStrs = waypoints.map(w => w.address || `${w.lat},${w.lng}`)
    url += `&waypoints=${encodeURIComponent(waypointStrs.join('|'))}`
  }

  url += '&travelmode=driving'

  return url
}

/**
 * Generate a Google Maps URL to a single destination
 *
 * @param {{lat: number, lng: number, address?: string}} destination - The destination
 * @returns {string} Google Maps URL
 */
export function generateSingleDestinationUrl(destination) {
  if (!destination) {
    return 'https://www.google.com/maps'
  }

  const destStr = destination.address || `${destination.lat},${destination.lng}`
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destStr)}&travelmode=driving`
}

/**
 * Reverse the order of stops
 *
 * @param {Array} stops - Array of stops
 * @returns {Array} Reversed array
 */
export function reverseRoute(stops) {
  return [...stops].reverse()
}

/**
 * Reorder stops based on an array of indices
 *
 * @param {Array} stops - Original stops array
 * @param {number[]} newOrder - Array of indices in new order
 * @returns {Array} Reordered stops
 */
export function reorderStops(stops, newOrder) {
  return newOrder.map(index => stops[index])
}
